import { radioStations, type RadioStation } from '@/mocks/radios';

// ─── Interfaz extendida para búsqueda ───
export interface SearchRadioStation {
  id: number;
  name: string;
  slug: string;
  frequency: string;
  city: string;
  state: string;
  country: string;
  category: string;
  language: string;
  announcers: string[];
  listeners: number;
  rating: number;
  isLive: boolean;
  logoUrl: string;
  description: string;
  cabinaMessages: number;
  totalLives: number;
  publications: number;
  score: number;
}

// ─── Metadatos de búsqueda por ID ───
const searchMeta: Record<number, Pick<SearchRadioStation, 'announcers' | 'totalLives' | 'publications' | 'score'>> = {
  1:  { announcers: ['Carlos Méndez', 'Ana Lucía Torres'],         totalLives: 87,  publications: 342, score: 89 },
  2:  { announcers: ['Pastor Miguel Ángel Ruiz', 'Hermana Gloria Castillo'], totalLives: 156, publications: 612, score: 95 },
  3:  { announcers: ['Pedro "El Gallo" Contreras', 'María Elena Salazar'], totalLives: 210, publications: 890, score: 97 },
  4:  { announcers: ['Jorge Alberto Navarro', 'Carolina Ríos', 'Daniel Fuentes'], totalLives: 45, publications: 1200, score: 82 },
  5:  { announcers: ['David "Metal" Hernández', 'Lucía Vargas'],    totalLives: 98,  publications: 567, score: 88 },
  6:  { announcers: ['Rosa Martínez', 'Felipe García'],             totalLives: 34,  publications: 178, score: 72 },
  7:  { announcers: ['Rafael "Comentarista" Ortiz', 'Sofía Mendoza'], totalLives: 178, publications: 756, score: 93 },
  8:  { announcers: ['Isabel Delgado', 'Thomas Wright'],            totalLives: 28,  publications: 234, score: 68 },
  9:  { announcers: ['DJ Alex Beat', 'Valentina Cruz'],             totalLives: 67,  publications: 445, score: 84 },
  10: { announcers: ['Manuel Gutiérrez', 'Carmen Rojas'],           totalLives: 22,  publications: 156, score: 65 },
  11: { announcers: ['Pastor Samuel Díaz', 'Rebeca Hernández'],     totalLives: 112, publications: 489, score: 90 },
  12: { announcers: ['El Compa Toño', 'Rosario Jiménez'],           totalLives: 145, publications: 678, score: 91 },
};

// ─── Estaciones auxiliares (solo en búsqueda, no en radios.ts) ───
const auxiliarySearchStations: SearchRadioStation[] = [
  {
    id: 13, name: 'Radio Jazz Vibes', slug: 'radio-jazz-vibes', frequency: '95.30 RFW',
    city: 'Nueva Orleans', state: 'Luisiana', country: 'Estados Unidos', category: 'Internacional', language: 'Inglés',
    announcers: ['Marcus Johnson', 'Ella Thompson'], listeners: 3890, rating: 4.5, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Jazz%20radio%20station%20logo%20with%20saxophone%20and%20retro%20microphone%2C%20circular%20design%20warm%20amber%20and%20deep%20brown%20colors%2C%20vintage%20broadcast%20emblem%2C%20sophisticated%20jazz%20club%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-13&orientation=squarish',
    description: 'Smooth jazz, bebop y los clásicos del género. La mejor selección de jazz internacional.',
    cabinaMessages: 980, totalLives: 56, publications: 312, score: 78,
  },
  {
    id: 14, name: 'Radio Salsa Brava', slug: 'radio-salsa-brava', frequency: '101.10 RFW',
    city: 'San Juan', state: '', country: 'Puerto Rico', category: 'Salsa', language: 'Español',
    announcers: ['Héctor "Timbal" Rivera', 'Yolanda Cruz'], listeners: 7890, rating: 4.7, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Salsa%20music%20radio%20station%20logo%20with%20congas%20and%20tropical%20palm%20tree%2C%20circular%20design%20vibrant%20orange%20yellow%20and%20red%20colors%2C%20Caribbean%20festive%20broadcast%20emblem%2C%20rhythmic%20tropical%20energy%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-14&orientation=squarish',
    description: 'El sabor de la salsa, el son cubano y la música caribeña que te hace bailar.',
    cabinaMessages: 2600, totalLives: 134, publications: 523, score: 92,
  },
  {
    id: 15, name: 'Deportes Radio Centro', slug: 'deportes-radio-centro', frequency: '108.50 RFW',
    city: 'Ciudad de México', state: 'CDMX', country: 'México', category: 'Deportes', language: 'Español',
    announcers: ['José Ramón Fernández', 'Gabriela Santos', 'Luis "El Analista" Peña'], listeners: 14200, rating: 4.9, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Mexican%20sports%20radio%20station%20logo%20with%20soccer%20stadium%20and%20microphone%2C%20circular%20design%20green%20white%20and%20red%20colors%2C%20passionate%20broadcast%20emblem%2C%20athletic%20energy%20feel%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-15&orientation=squarish',
    description: 'La pasión del deporte mexicano. Cobertura total de liga MX, NFL, MLB y más.',
    cabinaMessages: 5200, totalLives: 245, publications: 1020, score: 98,
  },
  {
    id: 16, name: 'Radio Noticias Global', slug: 'radio-noticias-global', frequency: '112.30 RFW',
    city: 'Bogotá', state: 'Cundinamarca', country: 'Colombia', category: 'Noticias', language: 'Español',
    announcers: ['Andrés Camacho', 'Mónica Velásquez'], listeners: 5670, rating: 4.4, isLive: false,
    logoUrl: 'https://readdy.ai/api/search-image?query=Global%20news%20radio%20station%20logo%20with%20world%20map%20and%20antenna%2C%20circular%20design%20navy%20blue%20and%20white%20professional%20colors%2C%20serious%20broadcast%20emblem%2C%20international%20journalism%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-16&orientation=squarish',
    description: 'Análisis y cobertura de noticias globales con perspectiva latinoamericana.',
    cabinaMessages: 1340, totalLives: 38, publications: 890, score: 76,
  },
  {
    id: 17, name: 'Radio Clásica FM', slug: 'radio-clasica-fm', frequency: '91.50 RFW',
    city: 'Buenos Aires', state: 'CABA', country: 'Argentina', category: 'Internacional', language: 'Español',
    announcers: ['Maestro Alberto Fuentes', 'Clara Benedetti'], listeners: 2340, rating: 4.6, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Classical%20music%20radio%20station%20logo%20with%20violin%20and%20piano%20keys%2C%20circular%20design%20elegant%20gold%20and%20cream%20colors%2C%20refined%20broadcast%20emblem%2C%20sophisticated%20concert%20hall%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-17&orientation=squarish',
    description: 'Mozart, Beethoven y los grandes maestros. Disfruta la música clásica como nunca.',
    cabinaMessages: 560, totalLives: 18, publications: 98, score: 60,
  },
  {
    id: 18, name: 'Radio Reggae Vibraciones', slug: 'radio-reggae-vibraciones', frequency: '132.80 RFW',
    city: 'Kingston', state: '', country: 'Jamaica', category: 'Reggae', language: 'Inglés',
    announcers: ['Rasta Mike', 'Shelly-Ann Brown'], listeners: 4100, rating: 4.3, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Reggae%20radio%20station%20logo%20with%20lion%20and%20rasta%20colors%2C%20circular%20design%20green%20gold%20and%20red%20colors%2C%20laid%20back%20island%20broadcast%20emblem%2C%20positive%20vibes%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-18&orientation=squarish',
    description: 'Reggae roots, dancehall y la buena vibra caribeña. One love, one radio.',
    cabinaMessages: 1450, totalLives: 72, publications: 345, score: 79,
  },
  {
    id: 19, name: 'Radio Cristiana Alfa y Omega', slug: 'radio-cristiana-alfa-y-omega', frequency: '88.30 RFW',
    city: 'Guatemala', state: 'Guatemala', country: 'Guatemala', category: 'Cristiana', language: 'Español',
    announcers: ['Pastor David Morales', 'Ester de Morales'], listeners: 6780, rating: 4.8, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Christian%20faith%20radio%20station%20logo%20with%20Bible%20and%20light%20rays%2C%20circular%20design%20sky%20blue%20and%20white%20colors%2C%20inspirational%20broadcast%20emblem%2C%20peaceful%20devotion%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-19&orientation=squarish',
    description: 'Palabra de Dios, alabanza y adoración para toda Centroamérica.',
    cabinaMessages: 3100, totalLives: 168, publications: 534, score: 94,
  },
  {
    id: 20, name: 'Radio Hip Hop Urbano', slug: 'radio-hip-hop-urbano', frequency: '105.70 RFW',
    city: 'Medellín', state: 'Antioquia', country: 'Colombia', category: 'Pop', language: 'Español',
    announcers: ['MC Flow Latino', 'DJ Sombra'], listeners: 5340, rating: 4.3, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Urban%20hip%20hop%20radio%20station%20logo%20with%20graffiti%20style%20microphone%20and%20boombox%2C%20circular%20design%20neon%20green%20and%20black%20colors%2C%20street%20culture%20broadcast%20emblem%2C%20edgy%20modern%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-20&orientation=squarish',
    description: 'Hip hop, trap, reggaetón y cultura urbana. El sonido de la calle en tus oídos.',
    cabinaMessages: 2100, totalLives: 89, publications: 467, score: 83,
  },
  {
    id: 21, name: 'Radio Country Estrella', slug: 'radio-country-estrella', frequency: '98.70 RFW',
    city: 'Nashville', state: 'Tennessee', country: 'Estados Unidos', category: 'Country', language: 'Inglés',
    announcers: ['Buck Johnson', 'Daisy May Carter'], listeners: 4560, rating: 4.5, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Country%20music%20radio%20station%20logo%20with%20acoustic%20guitar%20and%20cowboy%20hat%2C%20circular%20design%20warm%20brown%20and%20cream%20colors%2C%20rustic%20broadcast%20emblem%2C%20Nashville%20country%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-21&orientation=squarish',
    description: 'Country clásico y moderno desde la capital mundial del country. Nashville puro.',
    cabinaMessages: 1670, totalLives: 61, publications: 289, score: 77,
  },
  {
    id: 22, name: 'Radio Infantil Arcoíris', slug: 'radio-infantil-arcoiris', frequency: '93.10 RFW',
    city: 'Santiago', state: 'RM', country: 'Chile', category: 'Comunitaria', language: 'Español',
    announcers: ['Tía Carolina', 'Profesor Leo'], listeners: 3200, rating: 4.6, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Childrens%20radio%20station%20logo%20with%20rainbow%20and%20playful%20animal%20characters%2C%20circular%20design%20bright%20multicolor%20palette%2C%20cheerful%20educational%20broadcast%20emblem%2C%20friendly%20cartoon%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-22&orientation=squarish',
    description: 'Contenido educativo y divertido para los más pequeños. Cuentos, música y aprendizaje.',
    cabinaMessages: 890, totalLives: 42, publications: 203, score: 71,
  },
  {
    id: 23, name: 'Radio Frecuencia Rock', slug: 'radio-frecuencia-rock', frequency: '104.40 RFW',
    city: 'Barcelona', state: 'Cataluña', country: 'España', category: 'Rock', language: 'Español',
    announcers: ['Javier "Rockero" Pérez', 'Natalia Stone'], listeners: 6120, rating: 4.5, isLive: true,
    logoUrl: 'https://readdy.ai/api/search-image?query=Spanish%20rock%20radio%20station%20logo%20with%20electric%20guitar%20and%20Spanish%20flag%20colors%2C%20circular%20design%20red%20and%20yellow%20vibrant%20scheme%2C%20alternative%20broadcast%20emblem%2C%20energetic%20rocker%20aesthetic%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-23&orientation=squarish',
    description: 'Rock en español, indie, alternativo y los clásicos que marcaron generaciones.',
    cabinaMessages: 2300, totalLives: 103, publications: 590, score: 87,
  },
  {
    id: 24, name: 'Radio Noticias Al Día', slug: 'radio-noticias-al-dia', frequency: '110.20 RFW',
    city: 'Lima', state: 'Lima', country: 'Perú', category: 'Noticias', language: 'Español',
    announcers: ['Roberto Campos', 'Silvia Huamán'], listeners: 4890, rating: 4.3, isLive: false,
    logoUrl: 'https://readdy.ai/api/search-image?query=Peruvian%20news%20radio%20station%20logo%20with%20Andean%20mountains%20silhouette%20and%20microphone%2C%20circular%20design%20red%20and%20white%20colors%2C%20serious%20broadcast%20emblem%2C%20Latin%20American%20journalism%20style%2C%20isolated%20on%20transparent%20background%2C%20vector%20flat%20design&width=200&height=200&seq=logo-search-24&orientation=squarish',
    description: 'Las noticias más importantes del Perú y el mundo con análisis profundo.',
    cabinaMessages: 1150, totalLives: 30, publications: 720, score: 73,
  },
];

// ─── Construir estación de búsqueda desde la fuente única ───
function buildSearchStation(station: RadioStation): SearchRadioStation {
  const meta = searchMeta[station.id] || {
    announcers: [station.hostName],
    totalLives: Math.round(station.recentPosts * 1.5),
    publications: station.recentPosts * 4,
    score: Math.round(station.radioScore),
  };
  return {
    id: station.id,
    name: station.name,
    slug: station.slug,
    frequency: station.frequency,
    city: station.city,
    state: station.state,
    country: station.country,
    category: station.category,
    language: station.language,
    listeners: station.listeners,
    rating: station.rating,
    isLive: station.isLive,
    logoUrl: station.logoUrl,
    description: station.description,
    cabinaMessages: station.cabinaMessages,
    ...meta,
  };
}

// ─── Lista unificada: principales de radios.ts (id 1-12) + auxiliares (id 13-24) ───
export const searchRadioStations: SearchRadioStation[] = [
  ...radioStations.filter((s) => s.id >= 1 && s.id <= 12).map(buildSearchStation),
  ...auxiliarySearchStations,
];

export const searchFilters = [
  { key: 'live', label: 'En vivo', icon: 'ri-live-line' },
  { key: 'mostListened', label: 'Más escuchadas', icon: 'ri-headphone-line' },
  { key: 'Cristiana', label: 'Cristiana', icon: 'ri-cross-line' },
  { key: 'Regional', label: 'Regional', icon: 'ri-music-2-line' },
  { key: 'Noticias', label: 'Noticias', icon: 'ri-newspaper-line' },
  { key: 'Deportes', label: 'Deportes', icon: 'ri-football-line' },
  { key: 'Comunidad', label: 'Comunidad', icon: 'ri-group-line' },
];

export const allCategories = [
  'Todas', 'Cristiana', 'Regional Mexicana', 'Salsa', 'Pop', 'Rock',
  'Noticias', 'Deportes', 'Comunitaria', 'Internacional', 'Country', 'Reggae',
];

export const allLanguages = ['Todos', 'Español', 'Inglés'];

export const allCountries = [
  'Todos', 'Argentina', 'Chile', 'Colombia', 'España', 'Estados Unidos',
  'Guatemala', 'Jamaica', 'México', 'Perú', 'Puerto Rico',
];